import React, { useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

const STORAGE_KEY = "price-lists-meta-v1";

const HARD_CODED_ZONES = [
  "CABA",
  "Vicente López",
  "San Isidro",
  "Morón",
  "3 de Febrero",
  "Hurlingham",
  "Ituzaingó",
  "Quilmes",
  "Berazategui",
  "Lanús",
  "Lomas de Zamora",
  "Avellaneda",
  "Zona Sur",
  "Zona Oeste",
  "Zona Norte",
  "Zona Peligrosa",
] as const;

const RANGE_KEYS = ["caba", "gba1", "gba2", "gba3"] as const;
type RangeKey = (typeof RANGE_KEYS)[number];
type PriceListType = "store" | "driver";
type CoverageMode = "cp" | "zone";

type CoverageItem = {
  mode: CoverageMode;
  postalCodes: string;
  zone: string;
};

type Coverage = Record<RangeKey, CoverageItem>;

type FormState = {
  id: number | null;
  code: string;
  name: string;
  type: PriceListType;
  priceCABA: string | number;
  priceGBA1: string | number;
  priceGBA2: string | number;
  priceGBA3: string | number;
  coverage: Coverage;
};

type TariffApiRow = {
  id?: number;
  name?: string;
  type?: string;
  price_caba?: number | string;
  price_gba1?: number | string;
  price_gba2?: number | string;
  price_gba3?: number | string;
};

type PriceListItem = {
  id: number | null;
  code: string;
  name: string;
  rawName: string;
  type: PriceListType;
  priceCABA: number | string;
  priceGBA1: number | string;
  priceGBA2: number | string;
  priceGBA3: number | string;
  coverage: Coverage;
};

type MetaStorage = Record<
  string,
  {
    coverage: Coverage;
  }
>;

type TariffsResponse = {
  ok?: boolean;
  error?: string;
  message?: string;
  tariffs?: TariffApiRow[];
  tariff?: TariffApiRow;
  tariffID?: number;
  id?: number;
  data?: { id?: number } | TariffApiRow[] | TariffApiRow;
};

type ModalProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
};

const emptyCoverage = (): Coverage => ({
  caba: { mode: "cp", postalCodes: "", zone: "" },
  gba1: { mode: "cp", postalCodes: "", zone: "" },
  gba2: { mode: "cp", postalCodes: "", zone: "" },
  gba3: { mode: "cp", postalCodes: "", zone: "" },
});

const emptyForm: FormState = {
  id: null,
  code: "",
  name: "",
  type: "store",
  priceCABA: "",
  priceGBA1: "",
  priceGBA2: "",
  priceGBA3: "",
  coverage: emptyCoverage(),
};

function apiTypeToUiType(value?: string): PriceListType {
  const normalized = String(value || "").trim().toUpperCase();
  return normalized === "DRIVER" ? "driver" : "store";
}

function uiTypeToApiType(value: PriceListType): "DRIVER" | "STORE" {
  return value === "driver" ? "DRIVER" : "STORE";
}

function sanitizePostalCodes(value = "") {
  let cleaned = value.replace(/[^\d,]/g, "");
  cleaned = cleaned.replace(/,+/g, ",");
  cleaned = cleaned.replace(/^,/, "");
  return cleaned;
}

function isValidPostalCodes(value = "") {
  if (!value.trim()) return false;
  return /^\d+(,\d+)*$/.test(value.trim());
}

function readMetaStorage(): MetaStorage {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeMetaStorage(data: MetaStorage) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function buildMetaKey(id: number | null, rawName: string) {
  return id ? `id:${id}` : `name:${rawName}`;
}

function normalizeTariff(row: TariffApiRow): PriceListItem {
  const rawName = row?.name || "";
  const detectedType = apiTypeToUiType(row?.type);
  const storage = readMetaStorage();
  const metaKey = buildMetaKey(row?.id ?? null, rawName);
  const savedMeta = storage[metaKey] || null;

  return {
    id: row?.id ?? null,
    code: row?.id ? `T${row.id}` : "",
    name: rawName,
    rawName,
    type: detectedType,
    priceCABA: row?.price_caba ?? "",
    priceGBA1: row?.price_gba1 ?? "",
    priceGBA2: row?.price_gba2 ?? "",
    priceGBA3: row?.price_gba3 ?? "",
    coverage: savedMeta?.coverage || emptyCoverage(),
  };
}

function formatMoney(value: number | string | null | undefined) {
  if (value === null || value === undefined || value === "") return "—";
  return `$${Number(value).toLocaleString("es-AR")}`;
}

function saveCoverageMeta({
  id,
  rawName,
  coverage,
}: {
  id: number | null;
  rawName: string;
  coverage: Coverage;
}) {
  const storage = readMetaStorage();
  const key = buildMetaKey(id, rawName);
  storage[key] = { coverage };
  writeMetaStorage(storage);
}

function migrateCoverageMetaByNameToId({
  id,
  rawName,
}: {
  id: number | null;
  rawName: string;
}) {
  if (!id || !rawName) return;
  const storage = readMetaStorage();
  const byName = buildMetaKey(null, rawName);
  const byId = buildMetaKey(id, rawName);

  if (storage[byName] && !storage[byId]) {
    storage[byId] = storage[byName];
    delete storage[byName];
    writeMetaStorage(storage);
  }
}

function extractTariffRows(json: TariffsResponse): TariffApiRow[] {
  if (Array.isArray(json?.tariffs)) return json.tariffs;
  if (Array.isArray(json?.data)) return json.data;
  if (json?.tariff && typeof json.tariff === "object") return [json.tariff];
  if (json?.data && !Array.isArray(json.data) && typeof json.data === "object" && "name" in json.data) {
    return [json.data as TariffApiRow];
  }
  return [];
}

const pageWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const subtitleStyle: React.CSSProperties = {
  margin: "8px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "15px",
  lineHeight: 1.6,
};

const actionsWrapStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const kpiGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "16px",
};

const kpiLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginBottom: "10px",
};

const kpiValueStyle: React.CSSProperties = {
  fontSize: "28px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const kpiMetaStyle: React.CSSProperties = {
  marginTop: "10px",
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const sectionTextStyle: React.CSSProperties = {
  margin: "6px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.02)",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 12px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  background: "rgba(255,255,255,0.02)",
};

const tdStyle: React.CSSProperties = {
  padding: "14px 12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
};

const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,0.65)",
  padding: "16px",
};

const modalCardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "1280px",
  maxHeight: "85vh",
  overflow: "hidden",
  padding: 0,
};

const modalHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid var(--border)",
  padding: "18px 20px",
};

const modalBodyStyle: React.CSSProperties = {
  maxHeight: "75vh",
  overflowY: "auto",
  padding: "20px",
};

const softBoxStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "48px",
  display: "flex",
  alignItems: "center",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  padding: "0 16px",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "48px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  padding: "0 14px",
  fontSize: "14px",
};

function Modal({ open, title, onClose, children }: ModalProps) {
  if (!open) return null;

  return (
    <div style={modalOverlayStyle}>
      <div style={{ position: "absolute", inset: 0 }} onClick={onClose} />
      <div style={{ position: "relative", width: "100%", maxWidth: "1280px" }}>
        <Card style={modalCardStyle}>
          <div style={modalHeaderStyle}>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "26px",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  margin: "6px 0 0 0",
                  fontSize: "14px",
                  color: "var(--text-secondary)",
                }}
              >
                Definí valores por tramo y cómo querés identificar cada rango.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-secondary)",
                fontSize: "22px",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>

          <div style={modalBodyStyle}>{children}</div>
        </Card>
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: "block",
        marginBottom: "8px",
        fontSize: "12px",
        color: "var(--text-secondary)",
      }}
    >
      {children}
    </span>
  );
}

function Select({
  label,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  return (
    <label style={{ display: "block" }}>
      <FieldLabel>{label}</FieldLabel>
      <select {...props} style={selectStyle}>
        {children}
      </select>
    </label>
  );
}

function Segmented({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: CoverageMode) => void;
  options: Array<{ label: string; value: CoverageMode }>;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        borderRadius: "12px",
        border: "1px solid var(--border)",
        background: "var(--surface-alt)",
        padding: "4px",
      }}
    >
      {options.map((opt) => {
        const active = value === opt.value;

        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={{
              border: "none",
              cursor: "pointer",
              borderRadius: "10px",
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: active ? 700 : 500,
              background: active ? "var(--primary)" : "transparent",
              color: active ? "#000000" : "var(--text-secondary)",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function CoverageCell({
  rangeKey,
  coverage,
  onModeChange,
  onPostalChange,
  onZoneChange,
}: {
  rangeKey: RangeKey;
  coverage: Coverage;
  onModeChange: (rangeKey: RangeKey, mode: CoverageMode) => void;
  onPostalChange: (rangeKey: RangeKey, value: string) => void;
  onZoneChange: (rangeKey: RangeKey, value: string) => void;
}) {
  const current = coverage[rangeKey];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <Segmented
        value={current.mode}
        onChange={(mode) => onModeChange(rangeKey, mode)}
        options={[
          { label: "CP", value: "cp" },
          { label: "Zona", value: "zone" },
        ]}
      />

      {current.mode === "cp" ? (
        <div>
          <Input
            type="text"
            value={current.postalCodes}
            onChange={(e) => onPostalChange(rangeKey, e.target.value)}
            placeholder="Ej: 1001,1002,1003"
          />

          <p
            style={{
              margin: "6px 0 0 0",
              fontSize: "12px",
              color: "var(--text-secondary)",
            }}
          >
            Escribí los códigos postales, solo números separados por coma.
          </p>

          {current.postalCodes && !isValidPostalCodes(current.postalCodes) ? (
            <p
              style={{
                margin: "6px 0 0 0",
                fontSize: "12px",
                color: "var(--warning)",
              }}
            >
              Formato inválido. Usá solo números separados por coma.
            </p>
          ) : null}
        </div>
      ) : (
        <div>
          <select
            value={current.zone}
            onChange={(e) => onZoneChange(rangeKey, e.target.value)}
            style={selectStyle}
          >
            <option value="">Seleccionar zona</option>
            {HARD_CODED_ZONES.map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </select>

          <p
            style={{
              margin: "6px 0 0 0",
              fontSize: "12px",
              color: "var(--text-secondary)",
            }}
          >
            Luego esto se conectará con el apartado real de zonas.
          </p>
        </div>
      )}
    </div>
  );
}

export default function PriceLists() {
  const { token } = useAuth();

  const [allItems, setAllItems] = useState<PriceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [activeType, setActiveType] = useState<PriceListType>("store");
  const [searchCode, setSearchCode] = useState("");
  const [searchName, setSearchName] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [isEditing, setIsEditing] = useState(false);

  const loadTariffs = async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const res = await authFetch("/v2/tariffs", { token });
      const json: TariffsResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudieron cargar las tarifas");
      }

      const rows = extractTariffRows(json);
      const normalized = rows.map(normalizeTariff);

      setAllItems(normalized);
      return normalized;
    } catch (error) {
      console.error(error);
      const message =
        error instanceof Error ? error.message : "Error cargando listas de precios";
      setLoadError(message);
      setAllItems([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTariffs();
  }, [token]);

  const visibleItems = useMemo(() => {
    return allItems
      .filter((item) => item.type === activeType)
      .filter((item) =>
        searchCode.trim()
          ? item.code.toLowerCase().includes(searchCode.trim().toLowerCase())
          : true
      )
      .filter((item) =>
        searchName.trim()
          ? item.name.toLowerCase().includes(searchName.trim().toLowerCase())
          : true
      );
  }, [allItems, activeType, searchCode, searchName]);

  const totals = useMemo(() => {
    const stores = allItems.filter((item) => item.type === "store").length;
    const drivers = allItems.filter((item) => item.type === "driver").length;

    return {
      total: allItems.length,
      stores,
      drivers,
      visible: visibleItems.length,
    };
  }, [allItems, visibleItems]);

  const openCreateModal = (type: PriceListType) => {
    setForm({ ...emptyForm, type, coverage: emptyCoverage() });
    setIsEditing(false);
    setModalOpen(true);
  };

  const openEditModal = (item: PriceListItem) => {
    setForm({
      id: item.id,
      code: item.code,
      name: item.name,
      type: item.type,
      priceCABA: item.priceCABA,
      priceGBA1: item.priceGBA1,
      priceGBA2: item.priceGBA2,
      priceGBA3: item.priceGBA3,
      coverage: item.coverage || emptyCoverage(),
    });
    setIsEditing(true);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setForm({ ...emptyForm, coverage: emptyCoverage() });
    setIsEditing(false);
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setCoverageMode = (rangeKey: RangeKey, mode: CoverageMode) => {
    setForm((prev) => ({
      ...prev,
      coverage: {
        ...prev.coverage,
        [rangeKey]:
          mode === "cp"
            ? { mode: "cp", postalCodes: "", zone: "" }
            : { mode: "zone", postalCodes: "", zone: "" },
      },
    }));
  };

  const setCoveragePostalCodes = (rangeKey: RangeKey, rawValue: string) => {
    const sanitized = sanitizePostalCodes(rawValue);

    setForm((prev) => ({
      ...prev,
      coverage: {
        ...prev.coverage,
        [rangeKey]: {
          ...prev.coverage[rangeKey],
          postalCodes: sanitized,
        },
      },
    }));
  };

  const setCoverageZone = (rangeKey: RangeKey, zone: string) => {
    setForm((prev) => ({
      ...prev,
      coverage: {
        ...prev.coverage,
        [rangeKey]: {
          ...prev.coverage[rangeKey],
          zone,
        },
      },
    }));
  };

  const validateCoverage = () => {
    const labels: Record<RangeKey, string> = {
      caba: "CABA",
      gba1: "GBA 1",
      gba2: "GBA 2",
      gba3: "GBA 3",
    };

    for (const key of RANGE_KEYS) {
      const current = form.coverage[key];

      if (current.mode === "cp") {
        if (!isValidPostalCodes(current.postalCodes)) {
          return `Completá correctamente los CP de ${labels[key]}.`;
        }
      }

      if (current.mode === "zone") {
        if (!current.zone) {
          return `Seleccioná una zona para ${labels[key]}.`;
        }
      }
    }

    return null;
  };

  const validate = () => {
    if (!form.name.trim()) {
      alert("Completá el nombre de la lista.");
      return false;
    }

    const prices = [form.priceCABA, form.priceGBA1, form.priceGBA2, form.priceGBA3];

    for (const p of prices) {
      if (String(p).trim() === "") {
        alert("Completá los 4 valores.");
        return false;
      }
      if (Number.isNaN(Number(p))) {
        alert("Los valores deben ser numéricos.");
        return false;
      }
    }

    const coverageError = validateCoverage();
    if (coverageError) {
      alert(coverageError);
      return false;
    }

    return true;
  };

  const extractCreatedId = (json: TariffsResponse) => {
    return json?.tariffID || json?.id || (json?.data && !Array.isArray(json.data) ? json.data.id : null) || null;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);

    try {
      if (isEditing && form.id) {
        const payload = {
          tariff: {
            name: form.name.trim(),
            type: uiTypeToApiType(form.type),
            price_caba: Number(form.priceCABA),
            price_gba1: Number(form.priceGBA1),
            price_gba2: Number(form.priceGBA2),
            price_gba3: Number(form.priceGBA3),
          },
        };

        const res = await authFetch(`/v2/tariffs/${form.id}`, {
          method: "PUT",
          token,
          json: true,
          body: JSON.stringify(payload),
        });

        const json: TariffsResponse = await res.json().catch(() => ({}));

        if (!res.ok || json?.ok === false) {
          throw new Error(json?.error || "No se pudo actualizar la lista");
        }

        saveCoverageMeta({
          id: form.id,
          rawName: form.name.trim(),
          coverage: form.coverage,
        });
      } else {
        const payload = {
          name: form.name.trim(),
          type: uiTypeToApiType(form.type),
          price_caba: Number(form.priceCABA),
          price_gba1: Number(form.priceGBA1),
          price_gba2: Number(form.priceGBA2),
          price_gba3: Number(form.priceGBA3),
        };

        const res = await authFetch("/v2/tariffs", {
          method: "POST",
          token,
          json: true,
          body: JSON.stringify(payload),
        });

        const json: TariffsResponse = await res.json().catch(() => ({}));

        if (!res.ok || json?.ok === false) {
          throw new Error(json?.error || json?.message || "No se pudo crear la lista");
        }

        const createdId = extractCreatedId(json);

        saveCoverageMeta({
          id: createdId,
          rawName: form.name.trim(),
          coverage: form.coverage,
        });
      }

      const freshItems = await loadTariffs();
      const matched = freshItems.find(
        (x) =>
          x.rawName.trim().toLowerCase() === form.name.trim().toLowerCase() &&
          x.type === form.type
      );

      if (matched?.id) {
        migrateCoverageMetaByNameToId({
          id: matched.id,
          rawName: matched.rawName,
        });
      }

      closeModal();
    } catch (error) {
      console.error(error);
      alert((error as Error)?.message || "Error guardando la lista");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: PriceListItem) => {
    const ok = window.confirm(
      `¿Eliminar la lista "${item.name}" de ${item.type === "store" ? "stores" : "drivers"}?`
    );
    if (!ok || !item.id) return;

    try {
      const res = await authFetch(`/v2/tariffs/${item.id}`, {
        method: "DELETE",
        token,
      });

      const json: TariffsResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo eliminar la lista");
      }

      const storage = readMetaStorage();
      delete storage[buildMetaKey(item.id, item.rawName)];
      writeMetaStorage(storage);

      await loadTariffs();
    } catch (error) {
      console.error(error);
      alert(error instanceof Error ? error.message : "Error eliminando la lista");
    }
  };

  const clearFilters = () => {
    setSearchCode("");
    setSearchName("");
  };

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <div>
          <h1 style={titleStyle}>Listas de precios</h1>
          <p style={subtitleStyle}>
            Tarifas separadas para stores y drivers, con cobertura por CP o zona.
          </p>
        </div>

        <div style={actionsWrapStyle}>
          <Button variant="secondary" onClick={loadTariffs}>
            Recargar
          </Button>
          <Button onClick={() => openCreateModal(activeType)}>Nueva lista</Button>
        </div>
      </div>

      <div style={kpiGridStyle} className="price-lists-kpi-grid">
        <Card>
          <div style={kpiLabelStyle}>Total listas</div>
          <div style={kpiValueStyle}>{totals.total}</div>
          <div style={kpiMetaStyle}>Cantidad total registrada.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Listas stores</div>
          <div style={kpiValueStyle}>{totals.stores}</div>
          <div style={kpiMetaStyle}>Tarifas disponibles para stores.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Listas drivers</div>
          <div style={kpiValueStyle}>{totals.drivers}</div>
          <div style={kpiMetaStyle}>Tarifas disponibles para drivers.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Visibles</div>
          <div style={kpiValueStyle}>{totals.visible}</div>
          <div style={kpiMetaStyle}>Resultados según tipo y filtros.</div>
        </Card>
      </div>

      <Card>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "18px" }}>
          <button
            type="button"
            onClick={() => setActiveType("store")}
            style={{
              border: "none",
              cursor: "pointer",
              borderRadius: "999px",
              padding: "10px 16px",
              fontSize: "14px",
              fontWeight: 700,
              background: activeType === "store" ? "var(--primary)" : "var(--surface-alt)",
              color: activeType === "store" ? "#000000" : "var(--text-primary)",
            }}
          >
            Stores
          </button>

          <button
            type="button"
            onClick={() => setActiveType("driver")}
            style={{
              border: "none",
              cursor: "pointer",
              borderRadius: "999px",
              padding: "10px 16px",
              fontSize: "14px",
              fontWeight: 700,
              background: activeType === "driver" ? "var(--primary)" : "var(--surface-alt)",
              color: activeType === "driver" ? "#000000" : "var(--text-primary)",
            }}
          >
            Drivers
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: "16px",
            marginBottom: "20px",
          }}
          className="price-lists-filters-grid"
        >
          <div>
            <FieldLabel>Código</FieldLabel>
            <Input value={searchCode} onChange={(e) => setSearchCode(e.target.value)} />
          </div>

          <div>
            <FieldLabel>Nombre</FieldLabel>
            <Input value={searchName} onChange={(e) => setSearchName(e.target.value)} />
          </div>

          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <Button variant="secondary" fullWidth>
              Filtrar
            </Button>
          </div>

          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <Button variant="secondary" fullWidth onClick={clearFilters}>
              Limpiar filtros
            </Button>
          </div>
        </div>

        <div style={{ marginBottom: "14px" }}>
          <h2 style={sectionTitleStyle}>Tarifas registradas</h2>
          <p style={sectionTextStyle}>
            Listado general por tipo con edición y eliminación.
          </p>
        </div>

        {loadError ? (
          <div
            style={{
              marginBottom: "14px",
              color: "var(--danger)",
              fontSize: "14px",
            }}
          >
            {loadError}
          </div>
        ) : null}

        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Código</th>
                <th style={thStyle}>Nombre</th>
                <th style={{ ...thStyle, textAlign: "center" }}>CABA</th>
                <th style={{ ...thStyle, textAlign: "center" }}>GBA 1</th>
                <th style={{ ...thStyle, textAlign: "center" }}>GBA 2</th>
                <th style={{ ...thStyle, textAlign: "center" }}>GBA 3</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Tipo</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={8}
                    style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}
                  >
                    Cargando listas...
                  </td>
                </tr>
              ) : visibleItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}
                  >
                    No hay listas para este tipo.
                  </td>
                </tr>
              ) : (
                visibleItems.map((item) => (
                  <tr key={item.id ?? item.rawName}>
                    <td style={{ ...tdStyle, color: "var(--text-secondary)" }}>{item.code}</td>
                    <td style={tdStyle}>{item.name}</td>
                    <td style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}>
                      {formatMoney(item.priceCABA)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}>
                      {formatMoney(item.priceGBA1)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}>
                      {formatMoney(item.priceGBA2)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}>
                      {formatMoney(item.priceGBA3)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: "30px",
                          padding: "0 12px",
                          borderRadius: "999px",
                          fontSize: "12px",
                          fontWeight: 700,
                          background:
                            item.type === "store"
                              ? "var(--primary-soft)"
                              : "rgba(255,255,255,0.06)",
                          color: "var(--text-primary)",
                          border: "1px solid var(--border)",
                        }}
                      >
                        {item.type === "store" ? "Store" : "Driver"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          variant="secondary"
                          onClick={() => openEditModal(item)}
                          style={{ minHeight: "36px", padding: "8px 12px", fontSize: "12px" }}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => handleDelete(item)}
                          style={{ minHeight: "36px", padding: "8px 12px", fontSize: "12px" }}
                        >
                          Eliminar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={isEditing ? "Editar lista de precios" : "Nueva lista de precios"}
      >
        <form onSubmit={handleSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: "16px",
              marginBottom: "20px",
            }}
            className="price-lists-form-grid"
          >
            <div>
              <FieldLabel>Código</FieldLabel>
              <div style={softBoxStyle}>{form.code || "Se genera automáticamente"}</div>
            </div>

            <div>
              <FieldLabel>Nombre</FieldLabel>
              <Input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder={
                  form.type === "store"
                    ? "Ej: Tarifa store estándar"
                    : "Ej: Tarifa driver estándar"
                }
              />
            </div>

            <Select
              label="Tipo"
              value={form.type}
              onChange={(e) => setField("type", e.target.value as PriceListType)}
            >
              <option value="store">Store</option>
              <option value="driver">Driver</option>
            </Select>

            <div>
              <FieldLabel>Estado</FieldLabel>
              <div style={softBoxStyle}>Temporal: CP y zonas se guardan en este front.</div>
            </div>
          </div>

          <div style={tableWrapStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tramo</th>
                  <th style={thStyle}>CP / Zona</th>
                  <th style={thStyle}>Valor</th>
                </tr>
              </thead>

              <tbody>
                {[
                  { key: "caba" as RangeKey, label: "CABA", field: "priceCABA" as const },
                  { key: "gba1" as RangeKey, label: "GBA 1", field: "priceGBA1" as const },
                  { key: "gba2" as RangeKey, label: "GBA 2", field: "priceGBA2" as const },
                  { key: "gba3" as RangeKey, label: "GBA 3", field: "priceGBA3" as const },
                ].map((row) => (
                  <tr key={row.key}>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{row.label}</td>
                    <td style={tdStyle}>
                      <CoverageCell
                        rangeKey={row.key}
                        coverage={form.coverage}
                        onModeChange={setCoverageMode}
                        onPostalChange={setCoveragePostalCodes}
                        onZoneChange={setCoverageZone}
                      />
                    </td>
                    <td style={tdStyle}>
                      <Input
                        type="number"
                        step="0.01"
                        value={String(form[row.field])}
                        onChange={(e) => setField(row.field, e.target.value)}
                        placeholder="0"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div
            style={{
              marginTop: "24px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <Button type="button" variant="secondary" onClick={closeModal} disabled={saving}>
              Volver
            </Button>

            <Button type="submit" disabled={saving}>
              {saving ? "Guardando..." : isEditing ? "Guardar cambios" : "Guardar"}
            </Button>
          </div>
        </form>
      </Modal>

      <style>
        {`
          .price-lists-kpi-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          .price-lists-filters-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          .price-lists-form-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          @media (max-width: 1180px) {
            .price-lists-kpi-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }

            .price-lists-filters-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }

            .price-lists-form-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 720px) {
            .price-lists-kpi-grid,
            .price-lists-filters-grid,
            .price-lists-form-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
}